/**
 * Embeds Composable
 * Handles oEmbed URL detection and embedding for various providers
 */

import Trix from 'trix';

/**
 * Supported oEmbed providers with their URL patterns and embed configurations
 */
export const EMBED_PROVIDERS = {
  youtube: {
    name: 'YouTube',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/
    ],
    getEmbedUrl: (videoId) => `https://www.youtube.com/embed/${videoId}`,
    getThumbnail: (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    type: 'video'
  },
  vimeo: {
    name: 'Vimeo',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/
    ],
    getEmbedUrl: (videoId) => `https://player.vimeo.com/video/${videoId}`,
    oembedEndpoint: 'https://vimeo.com/api/oembed.json',
    type: 'video'
  },
  loom: {
    name: 'Loom',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?(?:www\.)?loom\.com\/embed\/([a-zA-Z0-9]+)/
    ],
    getEmbedUrl: (videoId) => `https://www.loom.com/embed/${videoId}`,
    type: 'video'
  },
  figma: {
    name: 'Figma',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?figma\.com\/(file|design|proto)\/([a-zA-Z0-9]+)/
    ],
    getEmbedUrl: (url) => `https://www.figma.com/embed?embed_host=feedbear&url=${encodeURIComponent(url)}`,
    type: 'iframe',
    aspectRatio: '16/9'
  },
  miro: {
    name: 'Miro',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?miro\.com\/app\/board\/([a-zA-Z0-9_=-]+)/
    ],
    getEmbedUrl: (boardId) => `https://miro.com/app/live-embed/${boardId}/`,
    type: 'iframe',
    aspectRatio: '16/9'
  },
  twitter: {
    name: 'Twitter',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?twitter\.com\/\w+\/status\/(\d+)/,
      /(?:https?:\/\/)?(?:www\.)?x\.com\/\w+\/status\/(\d+)/
    ],
    oembedEndpoint: 'https://publish.twitter.com/oembed',
    type: 'card'
  },
  github: {
    name: 'GitHub',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?github\.com\/([^\/]+)\/([^\/]+)\/(issues|pull|discussions)\/(\d+)/,
      /(?:https?:\/\/)?gist\.github\.com\/([^\/]+)\/([a-f0-9]+)/
    ],
    type: 'card'
  },
  notion: {
    name: 'Notion',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?notion\.so\/([a-zA-Z0-9-]+)/
    ],
    type: 'card'
  }
};

/**
 * Detect if a URL matches any supported embed provider
 * @param {string} url - The URL to check
 * @returns {Object|null} Provider info or null if not matched
 */
export function detectProvider(url) {
  if (!url || typeof url !== 'string') return null;

  for (const [providerId, provider] of Object.entries(EMBED_PROVIDERS)) {
    for (const pattern of provider.patterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          providerId,
          provider,
          match,
          url
        };
      }
    }
  }

  return null;
}

/**
 * Generate embed data for a URL
 * @param {string} url - The URL to embed
 * @returns {Promise<Object>} Embed data object
 */
export async function generateEmbedData(url) {
  const detected = detectProvider(url);

  if (!detected) {
    return {
      success: false,
      error: 'Unsupported URL'
    };
  }

  const { providerId, provider, match } = detected;

  try {
    const embedData = {
      success: true,
      provider: providerId,
      providerName: provider.name,
      originalUrl: url,
      type: provider.type
    };

    switch (providerId) {
      case 'youtube': {
        const videoId = match[1];
        embedData.embedUrl = provider.getEmbedUrl(videoId);
        embedData.thumbnail = provider.getThumbnail(videoId);
        embedData.videoId = videoId;
        break;
      }

      case 'vimeo': {
        const videoId = match[1];
        embedData.embedUrl = provider.getEmbedUrl(videoId);
        embedData.videoId = videoId;
        // Optionally fetch oEmbed data for thumbnail
        try {
          const oembedData = await fetchOEmbed(provider.oembedEndpoint, url);
          if (oembedData) {
            embedData.thumbnail = oembedData.thumbnail_url;
            embedData.title = oembedData.title;
          }
        } catch (e) {
          // Continue without thumbnail
        }
        break;
      }

      case 'loom': {
        const videoId = match[1];
        embedData.embedUrl = provider.getEmbedUrl(videoId);
        embedData.videoId = videoId;
        break;
      }

      case 'figma': {
        embedData.embedUrl = provider.getEmbedUrl(url);
        embedData.aspectRatio = provider.aspectRatio;
        break;
      }

      case 'miro': {
        const boardId = match[1];
        embedData.embedUrl = provider.getEmbedUrl(boardId);
        embedData.aspectRatio = provider.aspectRatio;
        break;
      }

      case 'twitter': {
        embedData.tweetId = match[1];
        // Fetch oEmbed for HTML
        try {
          const oembedData = await fetchOEmbed(provider.oembedEndpoint, url);
          if (oembedData) {
            embedData.html = oembedData.html;
          }
        } catch (e) {
          embedData.html = null;
        }
        break;
      }

      case 'github': {
        embedData.repoOwner = match[1];
        embedData.repoName = match[2];
        if (match[3]) {
          embedData.issueType = match[3];
          embedData.issueNumber = match[4];
        }
        break;
      }

      case 'notion': {
        embedData.pageId = match[1];
        break;
      }
    }

    return embedData;
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to generate embed'
    };
  }
}

/**
 * Fetch oEmbed data from a provider endpoint
 * @param {string} endpoint - The oEmbed endpoint URL
 * @param {string} url - The URL to fetch oEmbed for
 * @returns {Promise<Object>} oEmbed response data
 */
async function fetchOEmbed(endpoint, url) {
  const oembedUrl = `${endpoint}?url=${encodeURIComponent(url)}&format=json`;

  const response = await fetch(oembedUrl);
  if (!response.ok) {
    throw new Error('Failed to fetch oEmbed data');
  }

  return response.json();
}

/**
 * Initialize embed handling for a Trix editor
 * @param {HTMLElement} editorElement - The trix-editor element
 * @param {Object} options - Configuration options
 * @returns {Object} Object with methods and cleanup function
 */
export function useEmbeds(editorElement, options = {}) {
  if (!editorElement || !editorElement.editor) {
    console.warn('useEmbeds: Invalid editor element');
    return { insertEmbed: () => {}, cleanup: () => {} };
  }

  const editor = editorElement.editor;

  // NOTE: Paste handling is done in RichTextEditor.vue via the trix-paste event
  // to avoid duplicate embed insertion. Do not add a paste listener here.

  /**
   * Insert an embed into the editor
   * @param {string} url - The URL to embed
   */
  async function insertEmbed(url) {
    const embedData = await generateEmbedData(url);

    if (!embedData.success) {
      // If embed fails, just insert the URL as a link
      editor.insertString(url);
      return;
    }

    // Create a Trix attachment for the embed
    const attachment = new Trix.Attachment({
      content: generateEmbedHTML(embedData),
      contentType: 'application/vnd.feedbear.embed+html',
      url: embedData.originalUrl,
      embedData: JSON.stringify(embedData)
    });

    editor.insertAttachment(attachment);
  }

  /**
   * Generate HTML for an embed
   * @param {Object} embedData - The embed data object
   * @returns {string} HTML string
   */
  function generateEmbedHTML(embedData) {
    const { provider, type, embedUrl, originalUrl, title, thumbnail } = embedData;

    if (type === 'video') {
      return `
        <div class="embed-preview embed-preview--video" data-provider="${provider}">
          <div class="embed-preview__video">
            <iframe
              src="${embedUrl}"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
          <div class="embed-preview__meta">
            <span class="embed-preview__provider">${embedData.providerName}</span>
          </div>
        </div>
      `;
    }

    if (type === 'iframe') {
      return `
        <div class="embed-preview embed-preview--iframe" data-provider="${provider}" style="aspect-ratio: ${embedData.aspectRatio || '16/9'}">
          <iframe
            src="${embedUrl}"
            frameborder="0"
            allowfullscreen
          ></iframe>
        </div>
      `;
    }

    // Card type (Twitter, GitHub, Notion)
    if (embedData.html) {
      return `
        <div class="embed-preview embed-preview--card" data-provider="${provider}">
          ${embedData.html}
        </div>
      `;
    }

    // Fallback card
    return `
      <div class="embed-preview embed-preview--card" data-provider="${provider}">
        <a href="${originalUrl}" target="_blank" rel="nofollow noopener" class="embed-preview__link">
          ${thumbnail ? `<img src="${thumbnail}" class="embed-preview__thumbnail" alt="" />` : ''}
          <div class="embed-preview__content">
            <span class="embed-preview__title">${title || originalUrl}</span>
            <span class="embed-preview__provider">${embedData.providerName}</span>
          </div>
        </a>
      </div>
    `;
  }

  // Cleanup function (no-op since we don't attach listeners here)
  function cleanup() {
    // No listeners to clean up - paste handling is in RichTextEditor.vue
  }

  return {
    insertEmbed,
    detectProvider,
    generateEmbedData,
    cleanup
  };
}

export default useEmbeds;
